import type { MountedExperiment } from '@learnforge/demo-kit';
import { composeExpressions, parse, sampleCurve, type ExprNode, type Interval } from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import { stringify } from '@learnforge/demo-math/expr';
import type { FuncContext } from '../types';

export function compositionDomainIntervals(
  f: ExprNode,
  g: ExprNode,
  xMin: number,
  xMax: number,
  samples: number = 800,
): Interval[] {
  const composed = composeExpressions(f, g);
  const buf = sampleCurve(composed, xMin, xMax, samples);
  const intervals: Interval[] = [];
  let inRegion = false;
  let regionStart = xMin;
  for (let i = 0; i < buf.length; i++) {
    const x = xMin + ((xMax - xMin) * i) / (buf.length - 1);
    const ok = Number.isFinite(buf[i] as number);
    if (ok && !inRegion) {
      regionStart = x;
      inRegion = true;
    } else if (!ok && inRegion) {
      const prevX = xMin + ((xMax - xMin) * (i - 1)) / (buf.length - 1);
      intervals.push({ start: regionStart, end: prevX, openLeft: false, openRight: false });
      inRegion = false;
    }
  }
  if (inRegion) {
    intervals.push({ start: regionStart, end: xMax, openLeft: false, openRight: false });
  }
  return intervals;
}

export function mountComposition(ctx: FuncContext): MountedExperiment {
  let fSrc = 'sqrt(x)';
  let gSrc = '4 - x^2';
  let orderFG = true;

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };

  let graphHandle: FunctionGraphHandle | null = null;
  function redraw(): void {
    const fNode = parse(fSrc).node;
    const gNode = parse(gSrc).node;
    const composed = orderFG ? composeExpressions(fNode, gNode) : composeExpressions(gNode, fNode);
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const fSample = sampleCurve(fNode, view.xDomain[0], view.xDomain[1], sampleCount);
    const gSample = sampleCurve(gNode, view.xDomain[0], view.xDomain[1], sampleCount);
    const cSample = sampleCurve(composed, view.xDomain[0], view.xDomain[1], sampleCount);
    const shaded = compositionDomainIntervals(
      orderFG ? fNode : gNode,
      orderFG ? gNode : fNode,
      view.xDomain[0],
      view.xDomain[1],
    );
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      curves: [
        { sample: gSample, className: 'fn-curve-secondary', label: 'g(x)' },
        { sample: fSample, className: 'fn-curve-tertiary', label: 'f(x)' },
        { sample: cSample, className: 'fn-curve-primary', label: orderFG ? 'f∘g' : 'g∘f' },
      ],
      shadedDomain: shaded,
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    legend.setEntries([
      { id: 'f', formula: `f(x) = ${fSrc}`, color: 'var(--ink)' },
      { id: 'g', formula: `g(x) = ${gSrc}`, color: 'var(--red)', lineStyle: 'dashed' },
      {
        id: 'composition',
        formula: `${orderFG ? 'f∘g' : 'g∘f'}(x) = ${stringify(composed)}`,
        color: 'var(--accent)',
      },
    ]);
  }

  function makeExpressionInput(
    label: string,
    initial: string,
    onChange: (v: string) => void,
  ): void {
    const row = document.createElement('div');
    row.className = 'fn-panel-row';
    const l = document.createElement('label');
    l.textContent = label;
    const input = document.createElement('input');
    input.type = 'text';
    input.value = initial;
    input.addEventListener('change', () => {
      try {
        parse(input.value);
        onChange(input.value);
        input.classList.remove('fn-input-error');
      } catch {
        input.classList.add('fn-input-error');
      }
    });
    row.appendChild(l);
    row.appendChild(input);
    ctx.panel.appendChild(row);
  }

  makeExpressionInput(ctx.i18n.t('param.f.label'), fSrc, (v) => {
    fSrc = v;
    redraw();
  });
  makeExpressionInput(ctx.i18n.t('param.g.label'), gSrc, (v) => {
    gSrc = v;
    redraw();
  });

  const orderRow = document.createElement('div');
  orderRow.className = 'fn-panel-row';
  const orderLabel = document.createElement('label');
  orderLabel.textContent = ctx.i18n.t('param.order.label');
  const orderChoices = document.createElement('div');
  orderChoices.className = 'fn-composition-order';
  const orderButtons: HTMLButtonElement[] = [];
  function syncOrderButtons(): void {
    orderButtons.forEach((button) => {
      const active = (button.dataset.compositionOrder === 'fg') === orderFG;
      button.setAttribute('aria-pressed', String(active));
      button.dataset.active = String(active);
    });
  }
  for (const choice of [
    { id: 'fg', label: 'f ∘ g' },
    { id: 'gf', label: 'g ∘ f' },
  ]) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.compositionOrder = choice.id;
    button.textContent = choice.label;
    button.addEventListener('click', () => {
      orderFG = choice.id === 'fg';
      syncOrderButtons();
      redraw();
    });
    orderButtons.push(button);
    orderChoices.appendChild(button);
  }
  syncOrderButtons();
  orderRow.appendChild(orderLabel);
  orderRow.appendChild(orderChoices);
  ctx.panel.appendChild(orderRow);

  redraw();
  const viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'swap',
        labelKey: ctx.i18n.t('btn.swap'),
        onClick: () => {
          [fSrc, gSrc] = [gSrc, fSrc];
          redraw();
        },
      },
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          fSrc = 'sqrt(x)';
          gSrc = '4 - x^2';
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport.dispose();
      legend.destroy();
      svg.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
